import { useActionSheet } from '@expo/react-native-action-sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import * as ImagePicker from 'expo-image-picker';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Platform, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { AdminReservation } from '@/api';
import { IncidentalCategory } from '@/api';
import { useCreateIncidental } from '@/api/partners/incidentals/use-create-incidental';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import {
  prepareAssetForUpload,
  runWithConcurrencyLimit,
  snakeToTitleCase,
  toZonedISO,
  uniqByFilename,
  UPLOAD_MIME_TYPES,
} from '@/core';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { DatetimePopoverField } from '@/ui/datetime-popover-field';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';
import FileItem from '@/ui/file-item';
import { Icon } from '@/ui/icon';
import { Paperclip } from '@/ui/icons/paperclip';

const options: any[] = [
  {
    value: IncidentalCategory.PARKING_TICKET,
    title: snakeToTitleCase(IncidentalCategory.PARKING_TICKET),
    availableDayAfterTrip: true,
    amountVisible: true,
  },
  {
    value: IncidentalCategory.PARKING_RECEIPT,
    title: snakeToTitleCase(IncidentalCategory.PARKING_RECEIPT),
    availableDayAfterTrip: true,
    amountVisible: true,
  },
  {
    value: IncidentalCategory.SPEEDING_TICKET,
    title: snakeToTitleCase(IncidentalCategory.SPEEDING_TICKET),
    availableDayAfterTrip: true,
    amountVisible: true,
  },
  {
    value: IncidentalCategory.SMOKING,
    title: snakeToTitleCase(IncidentalCategory.SMOKING),
    availableDayAfterTrip: false,
    amountVisible: true,
    amount: 150,
    description: 'Pictures required to confirm validity.',
  },
  {
    value: IncidentalCategory.PET_HAIR,
    title: snakeToTitleCase(IncidentalCategory.PET_HAIR),
    availableDayAfterTrip: false,
    amountVisible: false,
    description: 'Pictures required to confirm validity.',
  },
  {
    value: IncidentalCategory.GLASS,
    title: snakeToTitleCase(IncidentalCategory.GLASS),
    availableDayAfterTrip: false,
    amountVisible: false,
    description:
      "Attach pictures of the damage, and if approved, we'll follow up with next steps.",
  },
  {
    value: IncidentalCategory.FLAT_TIRE,
    title: snakeToTitleCase(IncidentalCategory.FLAT_TIRE),
    availableDayAfterTrip: false,
    amountVisible: true,
    amount: 100,
    description:
      'If car is found to have a flat tire upon return, please submit images - this only applies to slow leaks, patch work, minor damage and cars that are found flat after return. Major blowouts that occur during a rental will have been handled by roadside directly while on rental.',
  },
  {
    value: IncidentalCategory.SCRATCHED_RIMS,
    title: snakeToTitleCase(IncidentalCategory.SCRATCHED_RIMS),
    availableDayAfterTrip: false,
    amountVisible: true,
    amount: 100,
    description:
      'Note that not all incidents are covered, before and after pictures necessary.',
  },
  {
    value: IncidentalCategory.ACCIDENT_BODY_DAMAGE,
    title: 'Accident/Body Damage',
    availableDayAfterTrip: false,
    amountVisible: false,
    requiredImageCount: 8,
    description:
      "Attach pictures of the damage, and if approved, we'll follow up with next steps. Before pictures required to have been uploaded at rental start for approval. Please upload at least 5 photos showing the car from all angles and at least 3 of the damaged area specifically.",
  },
  {
    value: IncidentalCategory.INTERIOR_DAMAGE,
    title: snakeToTitleCase(IncidentalCategory.INTERIOR_DAMAGE),
    availableDayAfterTrip: false,
    amountVisible: false,
    requiredImageCount: 8,
    description:
      "Examples: pet hair, liquids, etc. Attach pictures of the damage, and if approved, we'll follow up with next steps. Before pictures required to have been uploaded at rental start for approval. Please upload at least 5 photos showing the car from all angles and at least 3 of the damaged area specifically.",
  },
];

export const addIncidentalFormSchema = z
  .object({
    category: z.string(),
    offenseDate: z.date(),
    amount: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const selectedOption = options.find((opt) => opt.value === data.category);

    if (selectedOption?.amountVisible) {
      if (!data.amount || data.amount.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['amount'],
          message: 'Amount is required',
        });
      }
    }
  });

export type AddIncidentalFormType = z.infer<typeof addIncidentalFormSchema>;

type AddIncidentalFormProps = {
  category: string;
  offenseDate: Date;
  amount: string;
};

interface AddIncidentalModalProps {
  visible?: boolean;
  onClose: () => void;
  onSuccess: () => void;
  reservation: AdminReservation;
}

export function AddIncidentalModal({
  visible = false,
  onClose,
  onSuccess,
  reservation,
}: AddIncidentalModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(visible);
  const { showActionSheetWithOptions } = useActionSheet();
  const [showCalendar, setShowCalendar] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [images, setImages] = useState<ImagePicker.ImagePickerAsset[]>();
  const { mutateAsync: createIncidental, isPending: isCreatingIncidental } =
    useCreateIncidental();

  // Treat the date the user picks in the calendar as a wall-clock time in
  // the pickup location's zone — not the viewer's local zone. This keeps the
  // saved offense date consistent with how the incidentals table renders it.
  const pickupTz = reservation.pickupLocation?.timezone || 'UTC';

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    formState: { isValid },
    getValues,
    reset,
    watch,
  } = useForm<AddIncidentalFormType>({
    defaultValues: {
      category: options.find(
        (opt) => opt.value === IncidentalCategory.PARKING_TICKET
      )?.value,
      offenseDate: new Date(),
    },
    resolver: zodResolver(addIncidentalFormSchema),
  });

  const [category, offenseDate] = useWatch({
    control,
    name: ['category', 'offenseDate'],
  });

  useEffect(() => {
    setValue('amount', '');

    options.find((opt) => {
      if (opt.value === category && opt.amount) {
        setValue('amount', opt.amount.toString());
      }
    });
  }, [category]);

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  const formattedOptions = useMemo(() => {
    if (!reservation?.endTime) return options;

    const now = DateTime.now();
    const endTime = DateTime.fromISO(reservation.endTime);

    if (now > endTime.plus({ days: 1 })) {
      return options.filter((option) => option.availableDayAfterTrip);
    }

    return options;
  }, [reservation.endTime]);

  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === category);
  }, [category]);

  const handleUploadPhotos = async (replaceImageFileName = null) => {
    let imgs = replaceImageFileName
      ? images?.filter(
          (img) => (img.fileName || img.name) !== replaceImageFileName
        )
      : images;

    if (Platform.OS === 'web') {
      try {
        const result = await DocumentPicker.getDocumentAsync({
          type: UPLOAD_MIME_TYPES,
          copyToCacheDirectory: false,
          multiple: true,
        });

        if (!result.canceled) {
          const assets = await Promise.all(
            result.assets.map(async (asset) => {
              const response = await fetch(asset.uri);
              const blob = await response.blob();

              const file = new File([blob], asset.name || 'file.jpg', {
                type: asset.mimeType || 'image/jpeg',
              });

              return {
                file,
                name: asset.name,
                uri: asset.uri,
              };
            })
          );

          setImages(uniqByFilename([...(imgs || []), ...assets]));
        }
      } catch (error) {
        console.error('Error picking file:', error);
      }

      return;
    }

    const options = [
      'Choose from Photos',
      // 'Take a Photo',
      'Upload Files',
      'Cancel',
    ];
    const selectPhotoIdx = 0;
    // const takeAPhotoIdx = 1;
    const uploadFilesIdx = 1;
    const cancelButtonIndex = 2;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex: cancelButtonIndex,
      },
      async (selectedIndex: number) => {
        switch (selectedIndex) {
          // case takeAPhotoIdx: {
          //   const permission = await ImagePicker.getCameraPermissionsAsync();
          //   if (!permission.granted) {
          //     const permissionResult =
          //       await ImagePicker.requestCameraPermissionsAsync();

          //     if (!permissionResult.granted) {
          //       return;
          //     }
          //   }

          //   const result = await ImagePicker.launchCameraAsync({
          //     base64: true,
          //   });

          //   if (!result.canceled) {
          //     setImages(uniqByFilename([...imgs, ...result.assets]));
          //   }

          //   break;
          // }
          case uploadFilesIdx: {
            try {
              setUploadingPhotos(true);

              const result = await DocumentPicker.getDocumentAsync({
                type: UPLOAD_MIME_TYPES,
                copyToCacheDirectory: false,
                multiple: true,
              });

              if (!result.canceled) {
                const compressedResults = await runWithConcurrencyLimit(
                  result.assets.map(
                    (asset) => () => prepareAssetForUpload(asset)
                  ),
                  3 // concurrency limit
                );

                const formattedAssets = await Promise.all(
                  compressedResults.map(async (res, index) => {
                    const file: string = await FileSystem.readAsStringAsync(
                      res.uri,
                      {
                        encoding: FileSystem.EncodingType.Base64,
                      }
                    );

                    return {
                      ...result.assets[index],
                      fileName: result.assets[index].name,
                      base64: file,
                      uri: res.uri,
                    };
                  })
                );

                setImages(
                  uniqByFilename([...(imgs || []), ...formattedAssets])
                );
              }
            } catch (error) {
              console.error('Error picking file:', error);
            } finally {
              setUploadingPhotos(false);
            }
            break;
          }
          case selectPhotoIdx: {
            setUploadingPhotos(true);

            const result = await ImagePicker.launchImageLibraryAsync({
              mediaTypes: ImagePicker.MediaTypeOptions.Images,
              allowsEditing: false,
              allowsMultipleSelection: true,
              base64: true,
              selectionLimit: 15,
            });

            if (!result.canceled) {
              const compressedResults = await runWithConcurrencyLimit(
                result.assets.map(
                  (asset) => () => prepareAssetForUpload(asset)
                ),
                3 // concurrency limit
              );

              const formattedResults = compressedResults.map((res, index) => ({
                ...result.assets[index],
                uri: res.uri,
                base64: res.base64,
              }));

              setImages(uniqByFilename([...(imgs || []), ...formattedResults]));
            }

            setUploadingPhotos(false);
            break;
          }

          case cancelButtonIndex:
          // Canceled
        }
      }
    );
  };

  const handleCreateIncidental = async (data: AddIncidentalFormProps) => {
    try {
      const formattedImages =
        Platform.OS === 'web' ? images?.map((img) => img.file) : images;

      await createIncidental({
        reservationId: reservation.id,
        category: data.category,
        offenseDate: toZonedISO(data.offenseDate, pickupTz),
        amount: data.amount ? Math.round(parseFloat(data.amount) * 100) : 0,
        images: formattedImages,
      });

      setImages([]);
      reset();

      setShowModal(false);
      onSuccess();

      Toast.show({
        text1: 'Incidental created successfully!',
        type: 'success',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={{
        ...styles.container,
      }}
      onBackdropPress={() => {
        setShowModal(false);
        onClose?.();
      }}
    >
      <View style={styles.containerView}>
        <ScrollView
          style={{
            maxHeight: 700,
            ...(showCalendar &&
              Platform.OS === 'web' && { overflow: 'visible' }),
          }}
          contentContainerStyle={{
            gap: 16,
            flexGrow: 1,
          }}
          showsVerticalScrollIndicator={false}
        >
          <View
            style={{
              flexDirection: 'row',
              paddingTop: 16,
              paddingLeft: 16,
            }}
          >
            <View>
              <Text type="title2">Add Incidental</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                You can edit entries up to 3 months after rental end
              </Text>
            </View>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setShowModal(false);
                onClose?.();
              }}
              style={{
                position: 'absolute',
                right: 16,
                top: 16,
              }}
            />
          </View>

          <View
            style={{
              flex: 1,
              paddingTop: 16,
              zIndex: 1,
            }}
          >
            <ScrollView
              style={
                showCalendar && Platform.OS === 'web'
                  ? { overflow: 'visible' }
                  : undefined
              }
              contentContainerStyle={{
                flexGrow: 1,
              }}
              keyboardShouldPersistTaps="always"
              showsVerticalScrollIndicator={false}
            >
              <View style={{ flexGrow: 1, gap: 16, padding: 16 }}>
                <View style={{ zIndex: 2 }}>
                  <Dropdown
                    mode="single"
                    label="Incidental Type"
                    options={formattedOptions}
                    placeHolderText="Select incidental type"
                    onSelect={(selected: DropdownOption[]) => {
                      setValue('category', selected[0].value);
                      trigger();
                    }}
                    values={category ? [category] : []}
                  />
                </View>

                <View
                  style={{
                    zIndex: showCalendar ? 3 : 1,
                    position: 'relative',
                  }}
                >
                  <DatetimePopoverField
                    id="offense-date-calendar"
                    label="Date & time"
                    value={offenseDate}
                    minDate={new Date(2000, 0, 1)}
                    timeZone={pickupTz}
                    onChange={(d) => {
                      setValue('offenseDate', d);
                      trigger();
                    }}
                    onOpenChange={setShowCalendar}
                    containerStyle={{ width: 275 }}
                  />
                </View>

                {options.find((opt) => opt.value === category)
                  ?.amountVisible ? (
                  <ControlledInput
                    control={control}
                    name="amount"
                    label="Amount (USD)"
                    keyboardType="decimal-pad"
                    containerStyle={{ maxHeight: 100 }}
                    onBlur={() => {
                      trigger('amount');
                    }}
                    onChangeText={(text) => {
                      setValue('amount', text);
                      trigger('amount');
                    }}
                    disabled={
                      options.find((opt) => opt.value === category)?.amount
                    }
                  />
                ) : null}

                <View style={{ gap: 16 }}>
                  <Text type="bodyRegular">
                    Attachments (
                    {selectedOption.requiredImageCount
                      ? `at least ${selectedOption.requiredImageCount} attachments required`
                      : 'required'}
                    )
                  </Text>

                  {options.find((opt) => opt.value === category)
                    ?.description ? (
                    <View>
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {
                          options.find((opt) => opt.value === category)
                            ?.description
                        }
                      </Text>
                    </View>
                  ) : null}

                  <Button
                    text="Upload Files"
                    type="secondarySmall"
                    onPress={handleUploadPhotos}
                    leftIcon={
                      <Icon
                        type="naked"
                        icon={<Paperclip />}
                        width={12}
                        height={12}
                        fillColor={theme.colors.button.secondary.default.text}
                      />
                    }
                    style={{ width: 175 }}
                  />

                  {images?.map((image, index) => {
                    const imageFileName = image.fileName || image.name;
                    let imageUrl = image.uri;

                    return (
                      <FileItem
                        key={`${imageFileName}-${index}`}
                        imageUrl={imageUrl}
                        imageFileName={imageFileName}
                        onDelete={() => {
                          setImages(images.filter((_, i) => i !== index));
                        }}
                      />
                    );
                  })}
                </View>
              </View>
            </ScrollView>
          </View>

          <View
            style={{
              flexDirection: 'column',
              alignItems: 'flex-end',
              padding: 16,
              paddingTop: 0,
              gap: 24,
            }}
          >
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Button
                text="Cancel"
                type="ghost"
                onPress={() => onClose()}
                style={styles.button}
              />

              <Button
                text="Save"
                type="primary"
                onPress={handleSubmit(handleCreateIncidental)}
                style={styles.button}
                disabled={
                  !isValid ||
                  !images?.length ||
                  (selectedOption.requiredImageCount &&
                    images.length < selectedOption.requiredImageCount) ||
                  isCreatingIncidental
                }
              />
            </View>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    paddingBottom: {
      xs: 16,
      md: 0,
    },
    justifyContent: {
      xs: 'flex-end',
      md: 'center',
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 700,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_2,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    gap: 24,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
}));
